import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import STLThumbnail from '../components/STLThumbnail';
import SVGThumbnail from '../components/SVGThumbnail';

function HomePage({ onAddJobClick, onAdminClick, locationFilter, setLocationFilter, isLoggedIn }) {
  const [printers, setPrinters] = useState([]);
  const [laserCutters, setLaserCutters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [bambuStatus, setBambuStatus] = useState({});
  const [loading, setLoading] = useState(true);

  // Helper function to format seconds to readable time
  const formatTimeRemaining = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getContrastColor = (hex) => {
    const c = (hex || '#000000').replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.55 ? '#000000' : '#ffffff';
  };

  // Mark job as picked up
  const handleMarkPickedUp = async (jobId) => {
    try {
      await api.delete(`/jobs/${jobId}`);
      fetchData();
    } catch (err) {
      console.error('Error marking job as picked up:', err);
      alert('Failed to mark job as picked up');
    }
  };

  // Helper function to calculate relative time
  const getRelativeTime = (timestamp_ms) => {
    const now = Date.now();
    const diffMs = now - timestamp_ms;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Helper function to check if file is a zip
  const isZipFile = (filePath) => {
    return filePath && filePath.toLowerCase().endsWith('.zip');
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [printersRes, laserCuttersRes, jobsRes, errorsRes, completedRes, bambuRes] = await Promise.all([
        api.get('/printers'),
        api.get('/laser-cutters'),
        api.get('/jobs'),
        api.get('/errors'),
        api.get('/jobs/completed'),
        fetchBambuStatus() // Use new function that tries local gateway first
      ]);
      setPrinters(Array.isArray(printersRes.data) ? printersRes.data : []);
      setLaserCutters(Array.isArray(laserCuttersRes.data) ? laserCuttersRes.data : []);
      setJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
      setErrors(Array.isArray(errorsRes.data) ? errorsRes.data : []);
      setCompletedJobs(Array.isArray(completedRes.data) ? completedRes.data : []);
      setBambuStatus(bambuRes || {});
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  // Fetch Bambu status from VPS API (updated by local gateway)
  const fetchBambuStatus = async () => {
    try {
      const vpsRes = await api.get('/printers/bambu/status');
      console.log('[HomePage] Fetched Bambu data from VPS');
      return vpsRes.data || {};
    } catch (err) {
      console.error('[HomePage] Failed to fetch Bambu data:', err.message);
      return {};
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="home-page">
      <h2>Printers & Laser Cutters Status</h2>
      
      {/* Location Filter */}
      <div style={{ marginBottom: '15px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {['All', 'Drake', 'Garth'].map(location => (
          <button
            key={location}
            onClick={() => setLocationFilter(location)}
            style={{
              backgroundColor: locationFilter === location ? '#008CBA' : '#e0e0e0',
              color: locationFilter === location ? 'white' : 'black',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: locationFilter === location ? 'bold' : 'normal'
            }}
          >
            {location}
          </button>
        ))}
      </div>

      <div className="printers-grid">
        {/* 3D Printers */}
        {printers
          .filter(printer => locationFilter === 'All' || printer.location === locationFilter)
          .map(printer => {
            // Look up Bambu data by serial number
            const bambuData = printer.bambu_serial ? bambuStatus[printer.bambu_serial] : null;
            // System status is what's in the database (could be manual override or auto-calculated)
            const systemStatus = printer.status;
            // Printer status is the actual printer state from gateway
            const printerStatus = printer.printer_real_status || printer.status;
            const hasOverride = printer.manual_status_override && systemStatus !== printerStatus;
            const isOffline = bambuData && !bambuData.online;
            
            return (
            <div key={`printer-${printer.id}`} className={`printer-card ${printerStatus}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0 }}>⬛ {printer.name}</h3>
                  {printer.nozzle_size != null && parseFloat(printer.nozzle_size) !== 0.4 && (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center',
                      backgroundColor: '#212121', color: '#fff',
                      padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                      fontWeight: '800', userSelect: 'none',
                      letterSpacing: '0.3px', boxShadow: '0 1px 4px rgba(0,0,0,0.35)'
                    }}>
                      {parseFloat(printer.nozzle_size)} mm
                    </span>
                  )}
                  {printer.has_ams ? (
                    [1, 2, 3, 4].map(slot => {
                      const type = printer[`ams_slot_${slot}_type`];
                      const color = printer[`ams_slot_${slot}_color`] || '#999';
                      if (!type) return null;
                      if (type === 'empty') return (
                        <span key={slot} style={{ color: '#999' }}>-</span>
                      );
                      return (
                        <span key={slot} style={{
                          display: 'inline-flex', alignItems: 'center',
                          backgroundColor: color, color: getContrastColor(color),
                          padding: '1px 6px', borderRadius: '12px', fontSize: '10px',
                          fontWeight: 'bold', userSelect: 'none',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}>
                          {type.toUpperCase()}
                        </span>
                      );
                    })
                  ) : (
                    printer.filament_type && (
                      printer.filament_type === 'empty' ? (
                        <span style={{ color: '#999' }}>-</span>
                      ) : (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        backgroundColor: printer.filament_color || '#1976D2',
                        color: getContrastColor(printer.filament_color || '#1976D2'),
                        padding: '2px 8px', borderRadius: '12px', fontSize: '11px',
                        fontWeight: 'bold', userSelect: 'none',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }}>
                        {printer.filament_type.toUpperCase()}
                      </span>
                      )
                    )
                  )}
                </div>
              </div>
              {/* Status Badge + Current Job Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {isOffline ? (
                  <span className="status-badge" style={{ color: '#999', backgroundColor: '#e0e0e0' }}>Lost Connection</span>
                ) : hasOverride ? (
                  <>
                    <span className={`status-badge ${printerStatus}`}>{printerStatus.toUpperCase()}</span>
                    <span style={{ fontSize: '12px', backgroundColor: '#fff3cd', color: '#856404', padding: '4px 8px', borderRadius: '3px', fontWeight: 'bold' }}>
                      Admin: {systemStatus.toUpperCase()}
                    </span>
                  </>
                ) : (
                  <span className={`status-badge ${printerStatus}`}>{printerStatus.toUpperCase()}</span>
                )}
                {printer.current_job_id && (
                  <span style={{
                    fontSize: '11px',
                    backgroundColor: '#e3f2fd',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    color: '#1976d2',
                    fontWeight: '500'
                  }}>
                    👤 {printer.user_first_name} {printer.user_last_name?.[0] ? printer.user_last_name[0] + '.' : ''} • Job #{printer.current_job_id}
                  </span>
                )}
              </div>

              {/* Bambu Lab Status */}
              {bambuData && (
                <div style={{ 
                  backgroundColor: '#f0f0f0', 
                  padding: '10px', 
                  borderRadius: '4px', 
                  marginBottom: '10px',
                  fontSize: '12px',
                  lineHeight: '1.4'
                }}>
                  <div>🌡️ {Math.round(bambuData.nozzleTemp)}°C / {Math.round(bambuData.bedTemp)}°C | 📊 {Math.round(bambuData.progress || 0)}% | ⏱️ {bambuData.remainingTime ? `${bambuData.remainingTime} min` : 'N/A'}</div>
                  {bambuData.filename && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#666', wordBreak: 'break-all' }}>📁 {bambuData.filename}</div>
                  )}
                </div>
              )}

              {printer.current_job_id && isLoggedIn && (
                <div style={{ marginBottom: '10px' }}>
                  {isZipFile(printer.file_path) ? (
                    <div style={{ 
                      fontSize: '60px', 
                      textAlign: 'center', 
                      color: '#FFB300',
                      backgroundColor: '#f5f5f5',
                      padding: '20px',
                      borderRadius: '8px'
                    }}>
                      📁
                    </div>
                  ) : (
                    <STLThumbnail filePath={`/jobs/${printer.current_job_id}/preview`} width={160} height={120} />
                  )}
                </div>
              )}
            </div>
            );
          })}

        {/* Laser Cutters */}
        {laserCutters
          .filter(cutter => locationFilter === 'All' || cutter.location === locationFilter)
          .map(cutter => (
          <div key={`laser-${cutter.id}`} className={`printer-card ${cutter.status}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
              <h3 style={{ margin: 0 }}>⚡ {cutter.name}</h3>
            </div>
            <div className="printer-status">
              <span className={`status-badge ${cutter.status}`}>
                {cutter.status}
              </span>
            </div>
            {cutter.current_job_id && isLoggedIn && (
              <>
                <div style={{ marginBottom: '10px' }}>
                  <SVGThumbnail filePath={`/laser-jobs/${cutter.current_job_id}/preview`} width={160} height={120} />
                </div>
                <p><strong>Current Job:</strong> {cutter.user_first_name} {cutter.user_last_name?.[0] ? cutter.user_last_name[0] + '.' : ''}</p>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="jobs-section">
        <h2>Job Queue ({jobs.length})</h2>
        {jobs.length === 0 ? (
          <p>No jobs in queue</p>
        ) : (
          jobs.map(job => (
            <div key={job.id} className="job-card">
              {job.file_path && isLoggedIn && (
                <div style={{ marginBottom: '10px' }}>
                  {isZipFile(job.file_path) ? (
                    <div style={{ 
                      fontSize: '60px', 
                      textAlign: 'center', 
                      color: '#FFB300',
                      backgroundColor: '#f5f5f5',
                      padding: '20px',
                      borderRadius: '8px'
                    }}>
                      📁
                    </div>
                  ) : (
                    <STLThumbnail filePath={`/jobs/${job.id}/preview`} width={180} height={120} />
                  )}
                </div>
              )}
              <div className="job-info">
                <h4>{job.user_first_name} {job.user_last_name?.[0] ? job.user_last_name[0] + '.' : ''}</h4>
                <p><strong>Plastic:</strong> {job.plastic_type} | <strong>Nozzle:</strong> {job.nozzle_diameter}mm</p>
                <p><strong>Status:</strong> <span className={`status-badge ${job.status}`}>{job.status}</span></p>
                <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>Uploaded {getRelativeTime(job.timestamp_ms)}</p>
                {job.color && <p><strong>Color:</strong> {job.color}</p>}
              </div>
            </div>
          ))
        )}
      </div>

      {completedJobs.length > 0 && (
        <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#e8f5e9', borderRadius: '8px', border: '2px solid #4CAF50' }}>
          <h2 style={{ color: '#2e7d32', marginTop: 0 }}>✓ Ready for Pickup</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #4CAF50' }}>
                <th style={{ textAlign: 'left', padding: '10px', backgroundColor: '#c8e6c9' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '10px', backgroundColor: '#c8e6c9' }}>Pickup Location</th>
                <th style={{ textAlign: 'center', padding: '10px', backgroundColor: '#c8e6c9' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {completedJobs.map(job => (
                <tr key={job.id} style={{ borderBottom: '1px solid #c8e6c9' }}>
                  <td style={{ padding: '10px' }}>{job.user_first_name} {job.user_last_name?.[0] ? job.user_last_name[0] + '.' : ''}</td>
                  <td style={{ padding: '10px', backgroundColor: '#fff9c4' }}>{job.pickup_location || 'N/A'}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <button 
                      onClick={() => handleMarkPickedUp(job.id)}
                      style={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
                    >
                      Mark Picked Up
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {errors.length > 0 && (
        <div className="error-section">
          <h3>⚠️ Jobs with Errors</h3>
          {errors.map(error => (
            <div key={error.id} style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #f9897b' }}>
              <p><strong>{error.user_first_name} {error.user_last_name?.[0] ? error.user_last_name[0] + '.' : ''}'s Job:</strong></p>
              <p><strong>Error:</strong> {error.error_message}</p>
              {error.admin_comments && (
                <p><strong>Admin Notes:</strong> {error.admin_comments}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default HomePage;
